import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import isPropValid from '@emotion/is-prop-valid';
import { Box, styled } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { DeleteAutomationDialog } from './DeleteAutomationDialog';
import { EditAutomationModal } from './EditAutomationModal';
import { OrchestratorDropdownMenu } from './OrchestratorDropdownMenu';
import BackIcon from 'src/assets/images/actions-back-icon.svg';
import BackIconWhite from 'src/assets/images/actions-back-icon-white.svg';
import {
  useCloneWorkflowMutation,
  useGetWorkflowQuery,
} from 'src/services/orchestrator/orchestratorApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { Routes } from 'src/utils/enums';

interface OrchestratorHeaderProps {
  isDarkMode?: boolean;
  isFromNavbar?: boolean;
}

const OrchestratorHeader = ({
  isDarkMode = false,
  isFromNavbar = false,
}: OrchestratorHeaderProps) => {
  const { t } = useTranslation('orchestrator');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { workflowId } = useParams<{ workflowId: string }>();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: workflow } = useGetWorkflowQuery(
    { workflowId: workflowId || '' },
    { skip: !workflowId },
  );
  const [cloneWorkflow] = useCloneWorkflowMutation();

  const handleBackClick = () => {
    navigate(Routes.ORCHESTRATOR);
  };

  const handleDeleteSuccess = () => {
    navigate(Routes.ORCHESTRATOR);
  };

  const handleDuplicateClicked = async () => {
    if (!workflowId) {
      return;
    }
    try {
      const response = await cloneWorkflow(workflowId).unwrap();
      navigate(`${Routes.ORCHESTRATOR}/${response.workflow_id}`);
    } catch (error) {
      dispatch(
        setGlobalToastOptions({
          title: t('header.toastDuplicateError'),
          variant: 'danger',
        }),
      );
    }
  };

  return (
    <>
      <Header isDarkMode={isDarkMode} isFromNavbar={isFromNavbar}>
        <BackButton
          aria-label={t('header.backAriaLabel')}
          onClick={handleBackClick}
        >
          <img alt='' src={isDarkMode ? BackIconWhite : BackIcon} />
        </BackButton>
        {workflow?.workflow_name && (
          <>
            <Typography
              color={isDarkMode ? 'white' : undefined}
              variant='font24'
            >
              {workflow.workflow_name}
            </Typography>
            <Box
              sx={{
                height: '30px',
                marginLeft: '10px',
                width: '30px',
              }}
            >
              <OrchestratorDropdownMenu
                onDeleteClicked={() => setIsDeleteDialogOpen(true)}
                onDuplicateClicked={handleDuplicateClicked}
                onEditClicked={() => setIsEditModalOpen(true)}
                workflowId={workflowId || ''}
                workflowName={workflow.workflow_name}
              />
            </Box>
          </>
        )}
      </Header>
      <DeleteAutomationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onSuccess={handleDeleteSuccess}
        workflowId={workflowId || ''}
      />
      {workflow && (
        <EditAutomationModal
          initialDescription={workflow.workflow_description}
          initialName={workflow.workflow_name}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          workflowId={workflowId || ''}
        />
      )}
    </>
  );
};

export default OrchestratorHeader;

const Header = styled('div', {
  shouldForwardProp: prop =>
    isPropValid(String(prop)) &&
    prop !== 'isDarkMode' &&
    prop !== 'isFromNavbar',
})<{ isDarkMode: boolean; isFromNavbar: boolean }>`
  display: flex;
  justify-content: flex-start;
  padding: ${props => (props.isFromNavbar ? '0px' : '16px 24px 16px 40px')};
  background: ${props =>
    props.isDarkMode
      ? props.theme.palette.colors.primary[800]
      : props.theme.palette.background.default};
  border-bottom: ${props =>
    props.isFromNavbar
      ? '0px'
      : `1px solid ${props.theme.palette.colors.slate[200]}`};
  align-items: center;
  transition: inherit;
  flex: 1;
`;

const BackButton = styled('button')`
  margin-right: 20px;
  cursor: pointer;
  padding: 1px 2px 2px 2px;
  &:disabled {
    cursor: auto;
  }
  height: 26px;
  &:hover {
    background-color: transparent !important;
  }
`;
